import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildBrowsers, buildDevices, buildLanguages, buildOs, buildResolutions } from '../../../lib/analytics';
import { formatNumber } from '../../../lib/format';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

function shareRows(list: { name: string; value: number }[]) {
  const total = list.reduce((sum, i) => sum + i.value, 0) || 1;
  return list.map((i) => [i.name, formatNumber(i.value), `${Math.round((i.value / total) * 100)}%`]);
}

export default function EnvPage() {
  const { currentSite } = useSites();
  const { ranged, range } = useReportVisits(currentSite);
  const devices = buildDevices(ranged);
  const browsers = buildBrowsers(ranged);
  const os = buildOs(ranged);
  const langs = buildLanguages(ranged);
  const screens = buildResolutions(ranged);

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可查看设备、浏览器、系统、语言和分辨率。" />;
  }

  return (
    <div>
      <PageHeader
        title="系统环境"
        desc="识别访客使用的设备、浏览器、操作系统、语言和屏幕分辨率。"
        range={range}
        extra={
          <ExportButton
            filename={`${currentSite.domain}-env.csv`}
            columns={['分类', '名称', '浏览量', '占比']}
            rows={[
              ...shareRows(devices).map((r) => ['设备', ...r]),
              ...shareRows(browsers).map((r) => ['浏览器', ...r]),
              ...shareRows(os).map((r) => ['系统', ...r]),
              ...shareRows(langs).map((r) => ['语言', ...r]),
              ...shareRows(screens).map((r) => ['分辨率', ...r]),
            ]}
          />
        }
      />

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">设备类型</h2>
          </div>
          <StatTable columns={['设备', '浏览量', '占比']} rows={shareRows(devices)} />
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">浏览器</h2>
          </div>
          <StatTable columns={['浏览器', '浏览量', '占比']} rows={shareRows(browsers)} />
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">操作系统</h2>
          </div>
          <StatTable columns={['系统', '浏览量', '占比']} rows={shareRows(os)} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">浏览器语言</h2>
          </div>
          <StatTable columns={['语言', '浏览量', '占比']} rows={shareRows(langs)} empty="暂无语言数据" />
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">屏幕分辨率</h2>
          </div>
          <StatTable columns={['分辨率', '浏览量', '占比']} rows={shareRows(screens)} empty="暂无分辨率数据" />
        </div>
      </div>
    </div>
  );
}
